import * as scmpAccount from "@product/scmp-account-sdk";
import { useMountEffect } from "@react-hookz/web";
import React, { useCallback, useMemo } from "react";
import { getIsInWebView } from "scmp-sdk";
import { CloseButton } from "scmp-sdk/components/acquisition-popup-container/display/popups/common/close-button";
import type { WhyRegister } from "scmp-sdk/components/acquisition-popup-container/display/popups/why-register/types";
import { PopupBackdrop } from "scmp-sdk/components/acquisition-popup-container/display/styles";
import type { PopupProps } from "scmp-sdk/components/acquisition-popup-container/types";
import { useSdkContext } from "scmp-sdk/components/contexts";
import { Modal } from "scmp-sdk/components/modal";

import { getDestinationString } from "./actions";
import {
  AccountSignUpContainer,
  Container,
  ContentContainer,
  IconContainer,
  IntroductionContainer,
  IntroductionHTML,
  IntroductionLine,
  IntroductionNumber,
  OtherSignUpMethodMessage,
  StyledBookmark,
  StyledComment,
  StyledPlus,
  StyledScmpLogo,
  StyledStar,
  Title,
} from "./styles";

export type Props = PopupProps<{
  whyRegister: WhyRegister;
}>;

export const WhyRegisterPopup: React.FunctionComponent<Props> = ({
  modalContainerReference,
  onClose,
  whyRegister,
}) => {
  const introductions = whyRegister.content.introductions;

  // Note Introduction is an object which means the order may not be guaranteed and it uses 1,
  //      2,3 as keys so have added a.sort. Based on post below, the order
  //      should be fine without it
  //      https://stackoverflow.com/questions/5525795/does-javascript-guarantee-object-property-order/38218582#38218582
  const sortedIntroductionKeys = Object.keys(introductions).sort((a, b) => a.localeCompare(b));

  const { config, handleOpenLoginDialog, handleTrackGA4 } = useSdkContext();

  const destinationString = useMemo(
    () => getDestinationString(whyRegister.trackingActionName),
    [whyRegister.trackingActionName],
  );

  const reference = useCallback(
    (container: HTMLDivElement) => {
      if (!container) return;

      const accountUrl = new URL(config.accounts.host);

      scmpAccount.init({
        AUTH_CALLBACK_URL: `${config.accounts.authCallbackUrl}`,
        DEBUG_LOG: false,
        DESTINATION: destinationString,
        RECAPTCHA_CONTAINER_ID: "",
        RECAPTCHA_SITE_KEY: `${config.recaptcha.siteKey}`,
        REGISTER_CALLBACK_URL: `${config.accounts.registerCallbackUrl}`,
        SCMP_ACCOUNT_HOST: accountUrl.host,
        SCMP_ACCOUNT_PROTOCOL: accountUrl.protocol.slice(0, -1),
      });

      const isInWebView = getIsInWebView();
      scmpAccount.initLoginWidget(container.id, {
        destination: destinationString,
        loginDescription: "",
        loginWithEmail: false,
        sendWelcomeEmail: false,
        thirdPartySignInOptions: {
          triggerPoint: "ppto",
          withGoogle: !isInWebView,
        },
        title: "",
      });
    },
    [
      config.accounts.authCallbackUrl,
      config.accounts.host,
      config.accounts.registerCallbackUrl,
      config.recaptcha.siteKey,
      destinationString,
    ],
  );

  const campaignName = whyRegister.trackingActionName;

  useMountEffect(() => {
    handleTrackGA4({
      action: "imp",
      category: "promopopup",
      customized_parameters: {
        cta_type: "login_register",
        internal_campaign: campaignName,
        step: "1",
      },
      subcategory: "",
    });
  });

  const handleGoogleSignUpClick = () => {
    handleTrackGA4({
      action: "click",
      category: "promopopup",
      customized_parameters: {
        cta_type: "login_register",
        internal_campaign: campaignName,
        message: "Sign in with Google",
        step: "1",
      },
      subcategory: "",
    });
  };

  const handleOtherMethodSignUp = () => {
    handleTrackGA4({
      action: "click",
      category: "promopopup",
      customized_parameters: {
        cta_type: "login_register",
        internal_campaign: campaignName,
        message: "Sign in with other methods",
        step: "1",
      },
      subcategory: "",
    });

    onClose();

    handleOpenLoginDialog({
      destination: destinationString,
      ga4CustomParameter: {
        internal_campaign: campaignName,
        trigger_point: "ppto",
      },
    });
  };

  const handleCloseModal = () => {
    handleTrackGA4({
      action: "click",
      category: "promopopup",
      customized_parameters: {
        cta_type: "login_register",
        internal_campaign: campaignName,
        message: "close button",
        step: "1",
      },
      subcategory: "",
    });

    onClose();
  };

  return (
    <Modal
      BackdropComponent={PopupBackdrop}
      container={modalContainerReference?.current}
      fadeTimeout={400}
      open={true}
    >
      <Container>
        <CloseButton onClose={handleCloseModal} />
        <ContentContainer>
          <StyledScmpLogo />

          <Title dangerouslySetInnerHTML={{ __html: whyRegister.content.title }} />

          <IconContainer>
            <StyledPlus />
            <StyledBookmark />
            <StyledComment />
            <StyledStar />
          </IconContainer>

          <IntroductionContainer>
            {sortedIntroductionKeys.map(key => (
              <IntroductionLine key={key}>
                <IntroductionNumber>{`${key}. `}</IntroductionNumber>
                <IntroductionHTML dangerouslySetInnerHTML={{ __html: introductions[key] }} />
              </IntroductionLine>
            ))}
          </IntroductionContainer>

          <AccountSignUpContainer
            id="social-login-widget"
            onClick={handleGoogleSignUpClick}
            ref={reference}
          />

          <OtherSignUpMethodMessage onClick={handleOtherMethodSignUp}>
            Sign in with other methods
          </OtherSignUpMethodMessage>
        </ContentContainer>
      </Container>
    </Modal>
  );
};

WhyRegisterPopup.displayName = "WhyRegisterPopup";
